import { IRootStore, IUiStore } from '@shared/types';
import { makeAutoObservable } from 'mobx';

/** מפתח שמירת העדפות זרימת הקריאה הרציפה ב-localStorage */
const AUTO_FLOW_STORAGE_KEY = 'shanenli:auto-flow-preferences';

/** טווח מספר הקריאות המותר על אותו פסוק לפני מעבר לפסוק הבא */
export const MIN_READS_PER_VERSE = 1;
export const MAX_READS_PER_VERSE = 4;

type AutoFlowPreferences = {
  autoFlowEnabled: boolean;
  readsPerVerse: number;
};

const DEFAULT_AUTO_FLOW: AutoFlowPreferences = {
  autoFlowEnabled: false,
  readsPerVerse: 1,
};

const clampReadsPerVerse = (reads: number): number => {
  if (!Number.isFinite(reads)) return DEFAULT_AUTO_FLOW.readsPerVerse;
  return Math.min(MAX_READS_PER_VERSE, Math.max(MIN_READS_PER_VERSE, Math.round(reads)));
};

const loadAutoFlowPreferences = (): AutoFlowPreferences => {
  try {
    const raw = localStorage.getItem(AUTO_FLOW_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_AUTO_FLOW };

    const parsed = JSON.parse(raw);
    return {
      autoFlowEnabled: Boolean(parsed?.autoFlowEnabled),
      readsPerVerse: clampReadsPerVerse(Number(parsed?.readsPerVerse)),
    };
  } catch (error) {
    // אחסון חסום/פגום - נופלים לברירת המחדל בלי להפיל את האפליקציה
    return { ...DEFAULT_AUTO_FLOW };
  }
};

const saveAutoFlowPreferences = (preferences: AutoFlowPreferences): void => {
  try {
    localStorage.setItem(AUTO_FLOW_STORAGE_KEY, JSON.stringify(preferences));
  } catch (error) {
    // אחסון חסום (מצב פרטי וכד') - ההעדפה תישמר לזמן הסשן בלבד
  }
};

/** מפתח שמירת גודל הטקסט ב-localStorage */
const TEXT_SCALE_STORAGE_KEY = 'shanenli:text-scale';

/**
 * The reading text sizes a user can pick: as designed, a fifth bigger, two
 * fifths bigger. Three steps and no slider, so the choice is one tap and the
 * layout only ever has to look right at three sizes. Every reading face is
 * sized as calc(base * var(--reading-scale)) in _typography.scss, so one
 * number on the document root scales the words, the marks on them and the
 * line gaps together; a bigger size only wraps a breath line onto two screen
 * lines, it never moves a period break.
 */
export const TEXT_SCALES = [1, 1.2, 1.4] as const;
export type TextScale = (typeof TEXT_SCALES)[number];

const clampTextScale = (scale: number): TextScale =>
  (TEXT_SCALES as readonly number[]).includes(scale) ? (scale as TextScale) : 1;

const loadTextScale = (): TextScale => {
  try {
    return clampTextScale(Number(localStorage.getItem(TEXT_SCALE_STORAGE_KEY)));
  } catch (error) {
    return 1;
  }
};

const saveTextScale = (scale: TextScale): void => {
  try {
    localStorage.setItem(TEXT_SCALE_STORAGE_KEY, String(scale));
  } catch (error) {
    // אחסון חסום - הבחירה נשמרת לזמן הסשן בלבד
  }
};

/** The one place the scale reaches the page: a custom property on the root. */
const applyTextScale = (scale: TextScale): void => {
  if (typeof document === 'undefined') return;
  document.documentElement.style.setProperty('--reading-scale', String(scale));
};

/**
 * 🎨 UI STORE - Data Manager for User Interface States
 * 
 * **Primary Role:** Data Layer that manages global UI states
 * 
 * **Responsibilities:**
 * - Managing loading states
 * - Managing modal states
 * - Managing display preferences
 * 
 * **Interacts with:**
 * - No other stores - completely independent (Pure UI State Store)
 * - Receives calls from all controllers that need UI state
 * 
 * **Pattern:** State Manager - manages simple UI state
 * 
 * **Note:** This store holds only UI state without business logic
 */
class UiStore implements IUiStore {
  rootStore: IRootStore;
  isLoadingText = false;
  isLoadingShape = false;
  isDoneListeningDialogOpen = false;
  isMicrophoneOffDialogOpen = false;
  isShowSelectedEntityText = true;
  isGuestUser = false;

  /**
   * הצעה לאורח להירשם ולשמור את התוצאות שלו.
   * נקבעת כ-pending ברגע שהציון נשמר, ונפתחת רק כשהקריאה נגמרה, כדי לא
   * לקפוץ לילד באמצע פסוק.
   */
  isGuestSignInPromptPending = false;
  isGuestSignInPromptOpen = false;
  showByHeartOption = false; // Only show byHeart button if explicitly enabled

  /** גודל הטקסט בקריאה, בחירה של המשתמש: 1, 1.2 או 1.4 */
  textScale: TextScale = loadTextScale();

  /**
   * זרימה רציפה: בסיום פסוק האפליקציה ממשיכה לבד (חזרה על אותו פסוק או הפסוק הבא)
   * כשהמיקרופון כבר פתוח, במקום לחכות ללחיצה של המשתמש.
   */
  autoFlowEnabled = DEFAULT_AUTO_FLOW.autoFlowEnabled;

  /** כמה פעמים נקרא אותו פסוק לפני שהזרימה עוברת לפסוק הבא */
  readsPerVerse = DEFAULT_AUTO_FLOW.readsPerVerse;

  /**
   * ספירת "שלוש, שתיים, אחת" לפני שהקריאה מתחילה.
   * נותנת לשרת לפתוח את סשן התמלול, ולתלמיד רגע לאסוף אוויר.
   */

  // Navigation state management
  navigationState: {
    selectedBook: string;
    expandedAccordion: string | false;
  } | null = null;

  constructor(rootStore: IRootStore) {
    applyTextScale(this.textScale);
    makeAutoObservable(this);
    this.rootStore = rootStore;

    const savedAutoFlow = loadAutoFlowPreferences();
    this.autoFlowEnabled = savedAutoFlow.autoFlowEnabled;
    this.readsPerVerse = savedAutoFlow.readsPerVerse;
  }

  setIsLoadingText = (isLoading: boolean) => {
    // console.log({ isLoading });
    this.isLoadingText = isLoading;
  };

  setIsLoadingShape = (isLoading: boolean) => {
    this.isLoadingShape = isLoading;
  };

  setIsDoneListeningDialogOpen = (isDoneListeningDialogOpen: boolean) => {
    this.isDoneListeningDialogOpen = isDoneListeningDialogOpen;
  };

  setIsMicrophoneOffDialogOpen = (isMicrophoneOffDialogOpen: boolean) => {
    this.isMicrophoneOffDialogOpen = isMicrophoneOffDialogOpen;
  };

  tuggleIsShowSelectedEntityText = () => {
    this.isShowSelectedEntityText = !this.isShowSelectedEntityText;
  };

  setIsGuestUser = (isGuestUser: boolean) => {
    this.isGuestUser = isGuestUser;
  };

  setIsGuestSignInPromptPending = (pending: boolean) => {
    this.isGuestSignInPromptPending = pending;
  };

  setIsGuestSignInPromptOpen = (open: boolean) => {
    this.isGuestSignInPromptOpen = open;
  };

  setShowByHeartOption = (show: boolean) => {
    this.showByHeartOption = show;
  };

  setTextScale = (scale: number) => {
    this.textScale = clampTextScale(scale);
    saveTextScale(this.textScale);
    applyTextScale(this.textScale);
  };

  setAutoFlowEnabled = (enabled: boolean) => {
    this.autoFlowEnabled = enabled;
    this.persistAutoFlowPreferences();
  };

  setReadsPerVerse = (reads: number) => {
    this.readsPerVerse = clampReadsPerVerse(reads);
    this.persistAutoFlowPreferences();
  };

  private persistAutoFlowPreferences = () => {
    saveAutoFlowPreferences({
      autoFlowEnabled: this.autoFlowEnabled,
      readsPerVerse: this.readsPerVerse,
    });
  };

  // Navigation state management functions
  saveNavigationState = (book: string, accordion: string | false) => {
    this.navigationState = {
      selectedBook: book,
      expandedAccordion: accordion
    };
  };

  restoreNavigationState = () => {
    return this.navigationState;
  };

  clearNavigationState = () => {
    this.navigationState = null;
  };
}

export default UiStore;
